import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOPIC_META = {
  newcomers: {
    label: "Newcomer Essentials",
    dbLabel: "Newcomer Essentials",
    tagline: "Your first months in Canada, sorted.",
    desc: "The setup nobody walks you through: SIN, first bank account, credit from zero, and what to skip in those first 90 days.",
  },
  budgeting: {
    label: "Budgeting",
    dbLabel: "Budgeting",
    tagline: "Know where your money goes — without guilt.",
    desc: "Honest, flexible budgeting that fits real Canadian life: rent, groceries, transit, family support.",
  },
  "credit-building": {
    label: "Credit Building",
    dbLabel: "Credit Building",
    tagline: "Build a Canadian credit history from scratch.",
    desc: "How credit scores actually work in Canada, the right tools to start with, and the habits that move your score fastest.",
  },
  mortgages: {
    label: "Mortgages",
    dbLabel: "Mortgages",
    tagline: "Buying a home in Canada, demystified.",
    desc: "Down payments, stress tests, fixed vs. variable, CMHC insurance — what newcomers and first-time buyers need to understand.",
  },
  insurance: {
    label: "Insurance",
    dbLabel: "Insurance",
    tagline: "Protect what matters, skip what doesn't.",
    desc: "Health, tenant, auto, life, and disability — what's essential in Canada, what's optional, and how to avoid being oversold.",
  },
  "cost-of-living": {
    label: "Cost of Living",
    dbLabel: "Cost of Living",
    tagline: "Real numbers from real Canadian cities.",
    desc: "Honest monthly cost breakdowns for Toronto, Vancouver, Calgary, Montreal, and beyond.",
  },
};

export default function TopicPage() {
  const { topic } = useParams();
  const meta = TOPIC_META[topic] || { label: topic, dbLabel: topic, tagline: "", desc: "" };
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles?topic=${encodeURIComponent(meta.dbLabel)}`)
      .then((r) => { setArticles(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [topic, meta.dbLabel]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-3">{meta.label}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] tracking-tight mb-4">
            {meta.tagline}
          </h1>
          <p className="text-[#57534E] text-lg max-w-2xl leading-relaxed">{meta.desc}</p>
        </div>
      </section>

      {/* Articles */}
      <section className="py-12 px-6">
        <div className="max-w-screen-xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] rounded-lg p-6 animate-pulse h-40" />
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#57534E] text-lg">No articles yet for this topic.</p>
              <Link to="/articles" className="mt-4 inline-block text-[#1B4332] font-semibold hover:underline">
                Browse all articles
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/articles/${a.slug}`}
                  data-testid={`topic-article-${i}`}
                  className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group flex flex-col"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="topic-badge">{a.topic}</p>
                    <span className="text-[#A8A29E] text-xs">{a.read_time}</span>
                  </div>
                  <h3 className="font-bold text-[#1C1C1C] text-base leading-snug group-hover:text-[#1B4332] transition-colors mb-2 flex-1">
                    {a.title}
                  </h3>
                  <p className="text-[#57534E] text-sm leading-relaxed line-clamp-3 mb-4">{a.excerpt}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <p className="text-[#A8A29E] text-xs">
                      {new Date(a.created_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                    <ArrowRight size={14} className="text-[#1B4332] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
